import { Forma } from 'forma-embedded-view-sdk/auto';
import { AnalysisGroundGrid, NoiseAnalysis } from 'forma-embedded-view-sdk/dist/analysis';

export interface NoiseBand {
	m2: number;
	percent: number;
}

export interface NoiseBandThresholds {
	aboveM2: number;
	abovePercent: number;
	belowM2: number;
	belowPercent: number;
}

// @note: this will probably not be needed; currently, it is used as we have it in the original Forma noise analysis report.
export interface NoiseStandardBands {
	'<55dB': NoiseBand;
	'55–65dB': NoiseBand;
	'>65dB': NoiseBand;
	total: {
		m2: number;
	};
}

export interface NoiseStatistics {
	min: number;
	max: number;
	avg: number;
	validPoints: number;
	totalPoints: number;
}

export interface NoiseAnalysisData {
	noiseAnalysis: NoiseAnalysis;
	noiseGroundGrid: AnalysisGroundGrid;
	resolution: number;
	standardBands: NoiseStandardBands;
	statistics: NoiseStatistics;
	thresholds: Record<number, NoiseBandThresholds>;
}

const HEAT_MAP_NAME = 'noise-heat-map';

/**
 * Categorizes noise levels into standard bands and computes their areas.
 *
 * The function divides the grid into three bands:
 * - <55 dB (quiet)
 * - 55–65 dB (moderate)
 * - >65 dB (loud)
 *
 * @param grid - 1-D array of noise values in dB.
 * @param resolution - Length of one grid cell side in meters.
 * @returns Object with each band’s area in m² and its share of valid cells.
 */
const analyzeStandardBands = (grid: number[], resolution: number): NoiseStandardBands => {
	const cellAreaM2 = resolution * resolution;

	let below55 = 0;
	let between55and65 = 0;
	let above65 = 0;
	let totalValid = 0;

	grid.forEach((val) => {
		if (!isNaN(val)) {
			totalValid++;
			if (val < 55) {
				below55++;
			} else if (val >= 55 && val <= 65) {
				between55and65++;
			} else {
				above65++;
			}
		}
	});

	const totalArea = totalValid * cellAreaM2;

	return {
		'<55dB': {
			m2: below55 * cellAreaM2,
			percent: totalValid > 0 ? Math.round((below55 / totalValid) * 100) : 0,
		},
		'55–65dB': {
			m2: between55and65 * cellAreaM2,
			percent: totalValid > 0 ? Math.round((between55and65 / totalValid) * 100) : 0,
		},
		'>65dB': {
			m2: above65 * cellAreaM2,
			percent: totalValid > 0 ? Math.round((above65 / totalValid) * 100) : 0,
		},
		total: {
			m2: totalArea,
		},
	};
};

/**
 * Calculates the area (m²) and percentage of grid cells that fall above/below
 * each supplied noise threshold.
 *
 * @param grid        – 1-D array of noise-level values (dB); NaN values are ignored
 * @param resolution  – length of one grid cell side in metres
 * @param thresholds  – list of dB thresholds to test against
 *
 * @returns mapping threshold ➜ { aboveM2, abovePercent, belowM2, belowPercent }
 */
const analyzeThresholds = (grid: number[], resolution: number, thresholds: number[]) => {
	const cellAreaM2 = resolution * resolution;
	const validCount = grid.filter((val) => !isNaN(val)).length;

	return thresholds.reduce<Record<string, NoiseBandThresholds>>((acc, t) => {
		let above = 0;
		let below = 0;

		grid.forEach((val) => {
			if (!isNaN(val)) {
				if (val > t) above++;
				else below++;
			}
		});

		acc[t] = {
			aboveM2: above * cellAreaM2,
			abovePercent: validCount > 0 ? Math.round((above / validCount) * 100) : 0,
			belowM2: below * cellAreaM2,
			belowPercent: validCount > 0 ? Math.round((below / validCount) * 100) : 0,
		};
		return acc;
	}, {});
};

/**
 * Computes basic statistics for a numeric grid, ignoring NaN values.
 * All returned metrics are rounded to one decimal place.
 *
 * @param grid - Array of numeric values, possibly containing NaNs.
 * @returns Object with min, max, avg (all rounded), plus counts of valid and total points.
 */
const calculateStatistics = (grid: number[]): NoiseStatistics => {
	const validValues = grid.filter((v) => !isNaN(v));
	if (validValues.length === 0) {
		return { min: 0, max: 0, avg: 0, totalPoints: 0, validPoints: 0 };
	}

	const min = Math.min(...validValues);
	const max = Math.max(...validValues);
	const avg = validValues.reduce((sum, v) => sum + v, 0) / validValues.length;

	return {
		min: Math.round(min * 10) / 10,
		max: Math.round(max * 10) / 10,
		avg: Math.round(avg * 10) / 10,
		validPoints: validValues.length,
		totalPoints: grid.length,
	};
};

/**
 * Creates a canvas-based heat-map visualizing noise levels from a ground grid.
 *
 * The grid is normalized to 0-1 and mapped to a blue-green-red gradient:
 * - blue → green for the first third
 * - green → red for the remaining range
 * Transparent pixels are used for NaN values.
 *
 * @param noiseGroundGrid Object containing:
 *   - width  : number of columns
 *   - height : number of rows
 *   - grid   : flat array of noise readings (dB or any numeric unit)
 * @returns HTMLCanvasElement ready for display or texture use
 */
const createNoiseHeatMapCanvas = (noiseGroundGrid: AnalysisGroundGrid) => {
	const canvas = document.createElement('canvas');
	canvas.width = noiseGroundGrid.width;
	canvas.height = noiseGroundGrid.height;
	const ctx = canvas.getContext('2d');

	if (!ctx) {
		throw new Error('Unable to create canvas context');
	}

	let minNoise = Infinity;
	let maxNoise = -Infinity;

	for (let i = 0; i < noiseGroundGrid.grid.length; i++) {
		const noiseLevel = noiseGroundGrid.grid[i];
		if (!isNaN(noiseLevel)) {
			minNoise = Math.min(minNoise, noiseLevel);
			maxNoise = Math.max(maxNoise, noiseLevel);
		}
	}

	const imageData = ctx.createImageData(canvas.width, canvas.height);

	for (let i = 0; i < noiseGroundGrid.grid.length; i++) {
		const noiseLevel = noiseGroundGrid.grid[i];
		const pixelIndex = i * 4;

		if (isNaN(noiseLevel)) {
			imageData.data[pixelIndex + 3] = 0;
		} else {
			const normalized = (noiseLevel - minNoise) / (maxNoise - minNoise);

			let r, g, b;

			if (normalized < 0.33) {
				const t = normalized / 0.33;
				r = 0;
				g = Math.floor(t * 255);
				b = Math.floor((1 - t) * 255);
			} else if (normalized < 0.66) {
				const t = (normalized - 0.33) / 0.33;
				r = Math.floor(t * 255);
				g = 255;
				b = 0;
			} else {
				const t = (normalized - 0.66) / 0.34;
				r = 255;
				g = Math.floor((1 - t) * 255);
				b = 0;
			}

			imageData.data[pixelIndex] = r;
			imageData.data[pixelIndex + 1] = g;
			imageData.data[pixelIndex + 2] = b;
			imageData.data[pixelIndex + 3] = 200;
		}
	}

	ctx.putImageData(imageData, 0, 0);
	return canvas;
};

/**
 * Generates, positions and overlays a noise heat-map canvas on the site-limit terrain.
 *
 * 1. Runs the noise analysis to obtain a grid of SPL values (`noiseGroundGrid`).
 * 2. Creates an HTML canvas coloured by noise level.
 * 3. Calculates the bounding box of the first `site_limit` geometry.
 * 4. Centres the canvas on that bounding box and pins it to the terrain at the
 *    correct elevation and scale.
 *
 * @returns Object containing the heat-map name and the computed statistics, or
 *          `undefined` if no `site_limit` exists or the analysis fails.
 */
export const visualizeNoiseHeatMap = async () => {
	const { statistics, noiseGroundGrid } = await runNoiseAnalysis();
	if (!statistics) return;

	const canvas = createNoiseHeatMapCanvas(noiseGroundGrid);

	// Remove old canvas before adding new one
	Forma.terrain.groundTexture.remove({ name: HEAT_MAP_NAME });

	// Get site limit boundaries
	const siteLimitPaths = await Forma.geometry.getPathsByCategory({
		category: 'site_limit',
	});

	if (!siteLimitPaths || siteLimitPaths.length === 0) {
		console.error('No site limit found. Cannot position heat map correctly.');
		return;
	}

	// Get the first site limit path
	const siteLimitPath = siteLimitPaths[0];

	// Get the footprint with coordinates
	const siteLimitFootprint = await Forma.geometry.getFootprint({
		path: siteLimitPath,
	});
	const coordinates = siteLimitFootprint?.coordinates;

	if (!coordinates) {
		console.error('No coordinates found for site limit.');
		return;
	}

	// Calculate bounding box from coordinates
	let minX = Infinity,
		minY = Infinity,
		maxX = -Infinity,
		maxY = -Infinity;

	for (const [x, y] of coordinates) {
		minX = Math.min(minX, x);
		minY = Math.min(minY, y);
		maxX = Math.max(maxX, x);
		maxY = Math.max(maxY, y);
	}

	// Calculate dimensions and center
	const siteWidth = maxX - minX;
	const siteHeight = maxY - minY;
	const centerX = minX + siteWidth / 2;
	const centerY = minY + siteHeight / 2;

	// Get terrain elevation
	const elevation = await Forma.terrain.getElevationAt({
		x: centerX,
		y: centerY,
	});

	// Add ground texture positioned at site limit center
	await Forma.terrain.groundTexture.add({
		name: HEAT_MAP_NAME,
		canvas: canvas,
		position: {
			x: centerX,
			y: centerY,
			z: elevation,
		},
		scale: noiseGroundGrid.scale,
	});

	return {
		heatMapName: HEAT_MAP_NAME,
		statistics,
	};
};

/**
 * Removes the noise heat‑map texture from the terrain.
 *
 * @param {string} [name=HEAT_MAP_NAME] – The identifier of the heat‑map to delete.
 * @returns {Promise<void>} Resolves when the removal operation completes.
 */
export const removeNoiseHeatMap = async (name: string = HEAT_MAP_NAME) => {
	await Forma.terrain.groundTexture.remove({ name });
};

/**
 * Retrieves the latest successful noise analysis for the current proposal and
 * computes standard-band, threshold and statistical summaries over its
 * ground-level grid.
 *
 * @param thresholds - dB(A) values to evaluate exceedance areas
 *                     (default: [50, 55, 60, 65, 70])
 * @returns `NoiseAnalysisData` container with the raw analysis, grid,
 *          computed summaries and resolution.
 * @throws {Error} if no successful noise analysis exists or the grid is missing.
 */
export const runNoiseAnalysis = async (
	thresholds: number[] = [50, 55, 60, 65, 70]
): Promise<NoiseAnalysisData> => {
	try {
		const projectId = await Forma.getProjectId();

		const analyses = await Forma.analysis.list({ authcontext: projectId });
		const currentProposalId = await Forma.proposal.getId();

		const currentNoiseAnalysis = analyses
			.filter(
				({ proposalId, status }) =>
					proposalId === currentProposalId && status === 'SUCCEEDED'
			)
			.find(({ analysisType }) => analysisType === 'noise');

		if (!currentNoiseAnalysis) {
			throw new Error('There is no successful noise analysis for the current proposal.');
		}

		const noiseAnalysisData = await Forma.analysis.getNoiseAnalysis({
			analysisId: currentNoiseAnalysis.analysisId,
		});

		const noiseGroundGrid = await Forma.analysis.getGroundGrid({
			analysis: noiseAnalysisData,
		});

		if (!noiseGroundGrid?.grid) {
			throw new Error('There is no underlying data grid.');
		}

		const grid: number[] = Array.from(noiseGroundGrid.grid as Float32Array);
		const resolution =
			noiseAnalysisData.parameters?.groundResolution || noiseGroundGrid.resolution || 1;

		const standardBands = analyzeStandardBands(grid, resolution);
		const thresholdsResult = analyzeThresholds(grid, resolution, thresholds);
		const statistics = calculateStatistics(grid);

		return {
			noiseAnalysis: noiseAnalysisData,
			noiseGroundGrid,
			standardBands,
			thresholds: thresholdsResult,
			statistics,
			resolution,
		};
	} catch (err) {
		console.error('Error in noise analysis:', err);
		throw err;
	}
};
